import { useEffect } from 'react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { usePageHeader } from '../../providers/PageHeaderProvider';
import { NotificationsTable } from '../../components/monitoring/notifications/NotificationsTable';
import { RefreshingHint } from '../../components/monitoring/shared/RefreshingHint';
import { useNotificationsInbox } from '../../components/monitoring/notifications/useNotificationsInbox';
import type { DmNotificationsMeta } from '../../services/dmNotifications';
import {
    CLOSED_WINDOW,
    patientsAndEventsLabel,
    resolvedPatientsLabel
} from '../../components/monitoring/shared/monitoringCounts';

const DEFAULT_SUBTITLE = 'Dental Monitoring notifications waiting to be handled';
const CLOSED_SUBTITLE = `Dental Monitoring notifications ${CLOSED_WINDOW}`;

function headerSubtitle(meta: DmNotificationsMeta | null, closed: boolean): string {
    if (closed) return meta ? resolvedPatientsLabel(meta.total) : CLOSED_SUBTITLE;
    if (!meta) return DEFAULT_SUBTITLE;
    return patientsAndEventsLabel(meta.total, meta.totalNotifications, 'notification');
}

export function MonitoringNotifications() {
    usePageTitle('Monitoring · Notifications');
    const { setTitle, setSubtitle } = usePageHeader();
    const inbox = useNotificationsInbox();
    const subtitle = headerSubtitle(inbox.query.meta, inbox.table.status === 'closed');
    const refreshing = inbox.query.isShowingPrevious && !inbox.query.isLoading;

    useEffect(() => {
        setTitle('Notifications');
        return () => {
            setTitle(null);
            setSubtitle(null);
        };
    }, [setTitle, setSubtitle]);

    // Built here from primitives: a node made during render would re-set the header every render.
    useEffect(() => {
        setSubtitle(
            <>
                {subtitle}
                {refreshing && <RefreshingHint />}
            </>
        );
    }, [subtitle, refreshing, setSubtitle]);

    return (
        <div className="flex flex-col h-full overflow-hidden bg-gray-100">
            <NotificationsTable inbox={inbox} />
        </div>
    );
}
